import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Adaptador local con la firma de `ButtonPetra` (@ole/react-library-agents).
 *
 * Hoy renderiza el markup Aura del prototipo (`.btn-primary` / `.btn-secondary` / `.btn-ghost`).
 * Migración (OUW-72): reemplazar el `return` por `<ButtonPetra … />`. Los call sites no cambian.
 *
 * Divergencias respecto a `ButtonPetraProps` (documentadas en docs/client-portal/DS-MIGRATION.md):
 * - `href` / `to` / `target` / `rel`: renderiza `<a>` o `<Link>` con la misma clase. Petra no lo tiene.
 * - `size='sm'`: agrega `.btn-sm`. Petra tiene un solo tamaño.
 * - `className` / `style`: passthrough para hooks de layout del mock (`act-do`, `width:100%`).
 * - `data-spec` / `data-wired`: trazabilidad OUW-61 (specs.test.ts).
 * - `variant` mapea a las tres clases Aura; en Petra el equivalente es
 *   primary→'primary', secondary→'outline', ghost→'ghost'.
 */
export type PortalButtonVariant = 'primary' | 'secondary' | 'ghost';

export type PortalButtonProps = {
  label: string;
  variant?: PortalButtonVariant;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  title?: string;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
  id?: string;
  testId?: string;
  onClick?: () => void;
  // --- locales (no existen en ButtonPetra) ---
  size?: 'sm';
  href?: string;
  to?: string;
  target?: string;
  rel?: string;
  className?: string;
  style?: React.CSSProperties;
  'data-spec'?: string;
  'data-wired'?: boolean;
  'aria-busy'?: boolean;
};

const VARIANT_CLASS: Record<PortalButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
};

export function PortalButton({
  label,
  variant = 'primary',
  icon,
  iconPosition = 'left',
  disabled,
  title,
  fullWidth,
  type = 'button',
  id,
  testId,
  onClick,
  size,
  href,
  to,
  target,
  rel,
  className,
  style,
  'data-spec': dataSpec,
  'data-wired': dataWired,
  'aria-busy': ariaBusy,
}: PortalButtonProps) {
  const cls = [VARIANT_CLASS[variant], size === 'sm' ? 'btn-sm' : '', className || ''].filter(Boolean).join(' ');
  const mergedStyle: React.CSSProperties = {
    ...(fullWidth ? { width: '100%' } : null),
    ...style,
    ...(disabled && href ? { pointerEvents: 'none' } : null),
  };
  const content = (
    <>
      {icon && iconPosition === 'left' ? icon : null}
      {label}
      {icon && iconPosition === 'right' ? icon : null}
    </>
  );
  const shared = {
    id,
    className: cls,
    style: mergedStyle,
    'data-testid': testId,
    title,
    'data-spec': dataSpec,
    'data-wired': dataWired ? 'true' : undefined,
    'aria-busy': ariaBusy ? true : undefined,
  };

  if (to && !disabled) {
    return (
      <Link to={to} {...shared} onClick={onClick}>
        {content}
      </Link>
    );
  }

  // `<a>` ignores the native disabled attribute. OUW-88: honor `disabled` on the
  // href branch with aria-disabled + pointer-events, and drop onClick / href.
  if (href) {
    const blank = target === '_blank';
    return (
      <a
        href={disabled ? undefined : href}
        target={disabled ? undefined : target}
        rel={disabled ? undefined : (rel ?? (blank ? 'noopener noreferrer' : undefined))}
        aria-disabled={disabled || undefined}
        {...shared}
        onClick={disabled ? undefined : onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button type={type} disabled={disabled} {...shared} onClick={onClick}>
      {content}
    </button>
  );
}
